import { apiError } from "../../../../../../utils/formErrors";
import TableSkeleton from "../../../../../../component/TableSkeleton";
import React, { useState, useEffect } from "react";
import { Box, Stack, Table, TableHead, TableBody, TableRow, TableCell, Dialog, IconButton } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import FormField from "../../../../../../component/Formfield";
import EmptyState from "../../../../../../component/EmptyState";
import { StatusChip, headCellSx, bodyCellSx } from "../../../../../../component/listing";
import { getClients, createClient, deleteClient } from "../../../../../../services/timesheet";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const TONE = { active: "success", inactive: "muted" };

const Client = () => {
  const notify = useNotify();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", currency: "INR", email: "", contactName: "" });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true); setError(false);
    try {
      const res = await getClients();
      if (res?.success === false) throw apiError(res, "Failed to load clients");
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) { setError(true); notify.error(e?.message || "Failed to load clients"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    if (!form.name.trim()) { notify.error("Client name is required"); return; }
    setSaving(true);
    try {
      const res = await createClient(form);
      if (res?.success === false) throw apiError(res, "Could not create client");
      notify.success("Client added");
      setOpen(false); setForm({ name: "", currency: "INR", email: "", contactName: "" });
      fetchData();
    } catch (e) { notify.error(e?.message || "Could not create client"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await deleteClient(id); notify.success("Client removed"); fetchData(); }
    catch (e) { notify.error(e?.message || "Could not remove client"); }
  };

  const ch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Box sx={{ width: "100%", padding: "1.5rem 1.25rem" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Clients</Box>
        <BtnPrimary onClick={() => setOpen(true)} sx={{ borderRadius: "8px" }}>Add Client</BtnPrimary>
      </Stack>

      {loading ? (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Name</TableCell>
              <TableCell sx={headCellSx}>Currency</TableCell>
              <TableCell sx={headCellSx}>Contact</TableCell>
              <TableCell sx={headCellSx}>Email</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={{ ...headCellSx, width: "4rem" }}></TableCell>
            </TableRow>
          </TableHead>
            <TableBody><TableSkeleton columns={6} /></TableBody>
          </Table>
        ) : error ? (
        <EmptyState variant="error" title="Couldn't load clients" onRetry={fetchData} />
      ) : rows.length === 0 ? (
        <EmptyState title="No clients yet" description="Add clients to organize billable projects." />
      ) : (
        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Name</TableCell>
              <TableCell sx={headCellSx}>Currency</TableCell>
              <TableCell sx={headCellSx}>Contact</TableCell>
              <TableCell sx={headCellSx}>Email</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={{ ...headCellSx, width: "4rem" }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c._id}>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{c.name}</TableCell>
                <TableCell sx={bodyCellSx}>{c.currency}</TableCell>
                <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>{c.contactName || "—"}</TableCell>
                <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>{c.email || "—"}</TableCell>
                <TableCell sx={bodyCellSx}><StatusChip label={c.status || "active"} tone={TONE[c.status] || "success"} /></TableCell>
                <TableCell sx={bodyCellSx}>
                  <IconButton aria-label="Delete" size="small" onClick={() => remove(c._id)} sx={{ color: tokens.textMuted, "&:hover": { color: tokens.danger } }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { borderRadius: "12px", p: "1.5rem", width: "28rem", maxWidth: "94vw" } }}>
        <Box sx={{ fontSize: "1rem", fontWeight: 600, mb: "1.25rem" }}>Add Client</Box>
        <FormField label="Client Name" type="text" value={form.name} onChange={(e) => ch("name", e.target.value)} fullwidth />
        <FormField label="Currency" type="select" options={CURRENCIES.map((c) => ({ name: c, value: c }))} value={form.currency} onChange={(e) => ch("currency", e.target.value)} fullwidth />
        <FormField label="Contact Name" type="text" value={form.contactName} onChange={(e) => ch("contactName", e.target.value)} fullwidth />
        <FormField label="Email" type="text" value={form.email} onChange={(e) => ch("email", e.target.value)} fullwidth />
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: "1.5rem" }}>
          <BtnSecondary onClick={() => setOpen(false)} disabled={saving} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary onClick={submit} disabled={saving} sx={{ borderRadius: "8px" }}>{saving ? "Adding…" : "Add"}</BtnPrimary>
        </Stack>
      </Dialog>
    </Box>
  );
};

export default Client;
